'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAtom, useAtomValue } from 'jotai';
import ProfileContent from './ProfileContent';
import { profileCombinedDataAtom } from '@/app/atoms/profileCombinedDataAtom';
import { MergedDelegateInfo } from '@/app/atoms/combinedDataAtom';
import AnimatedSVG from '@/app/components/AnimatedSvg';
import { userVotesAtom, userVotesLoadingAtom } from '@/app/atoms/userData';
import VotesTable from './VotesTable';
import Loader from '@/app/components/Loader';
import { useFetchVoterVotes } from '@/app/hooks/useFetchVotesByVoter';
import { Vote, VoteEnum, ProposalCard } from '@/app/types/proposals';
import VoteStats from './VoteStats';
import { VoteStats as VoteStatsType } from './VoteStats';
import { allProposalsAtom } from '@/app/atoms/proposals';
import ProposalsTable from './ProposalTable';
import { State } from '@/app/types/proposals';
import { useWebSocket } from '@/app/hooks/useWebSocket';

const ProfilePage = () => {
  const [selectedTab, setSelectedTab] = useState<'votes' | 'proposals'>('votes');
  const [user, setUser] = useState<MergedDelegateInfo | null>(null);
  const [userInfo, setUserInfo] = useState<MergedDelegateInfo | null>(null);
  const pathname = usePathname();
  const userAddress = pathname.split('/')[4] as `0x${string}`;
  const [profiles] = useAtom(profileCombinedDataAtom);
  const allProposals = useAtomValue(allProposalsAtom);

  useFetchVoterVotes(userAddress);

  const userVotes = useAtomValue(userVotesAtom);
  const loadingUserVotes = useAtomValue(userVotesLoadingAtom);

  const userProposals = allProposals.filter(
    (proposal) => proposal.proposer.toLowerCase() === userAddress.toLowerCase(),
  );

  const fetchUser = () => {
    const userInfo = profiles.find(
      (user: any) => user.address === userAddress || user.wallet_address === userAddress,
    );
    if (userInfo) {
      setUserInfo(userInfo as MergedDelegateInfo);
    }
  };

  const onProposalsUpdated = useCallback(() => {
    fetchUser();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useWebSocket({
    onProposalsUpdated,
  });

  useEffect(() => {
    fetchUser();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [profiles, userAddress]);

  const calculateVoteStats = (votes: Vote[]): VoteStatsType => {
    return votes.reduce(
      (stats: VoteStatsType, vote: any) => {
        if (vote.vote === VoteEnum.YES) {
          stats.votedYes++;
        } else if (vote.vote === VoteEnum.NO) {
          stats.votedNo++;
        } else if (vote.vote === VoteEnum.ABSTAIN) {
          stats.votedAbstain++;
        }

        return stats;
      },
      {
        proposalsCreated: allProposals.filter(
          (proposal) => proposal.proposer === userAddress,
        ).length,
        votedYes: 0,
        votedNo: 0,
        votedAbstain: 0,
      },
    );
  };

  const calculateProposalStats = (proposals: ProposalCard[]) => {
    return proposals
      .filter((proposal) => proposal.proposer === userAddress)
      .reduce(
        (stats, proposal) => {
          switch (proposal.state.text.toLowerCase()) {
            case State.Succeeded:
            case State.Executed:
            case State.Passed:
              stats.succeeded++;
              break;
            case State.Defeated:
            case State.Failed:
              stats.failed++;
              break;
            case State.Active:
              stats.active++;
              break;
            case State.Pending:
              stats.pending++;
              break;
            case State.Canceled:
              stats.canceled++;
              break;
          }
          return stats;
        },
        {
          succeeded: 0,
          failed: 0,
          active: 0,
          pending: 0,
          canceled: 0,
        },
      );
  };

  const voteStats = userVotes ? calculateVoteStats(userVotes) : null;
  const proposalStats = userProposals ? calculateProposalStats(userProposals) : null;

  if (!userInfo) {
    return (
      <div className="flex min-w-screen min-h-[200px] items-center justify-center">
        <AnimatedSVG />
      </div>
    );
  }

  return (
    <div className="">
      <div className="no-scrollbar max-w-[80vw] mx-auto">
        {!voteStats ? (
          <Loader />
        ) : (
          <ProfileContent delegate={userInfo as MergedDelegateInfo} />
        )}
        {voteStats && proposalStats && (
          <VoteStats voteStats={voteStats} proposalStats={proposalStats} />
        )}

        <div className="mt-8">
          <div className="flex border-b border-neutral-700">
            <button
              className={`px-4 py-2 text-xl ${
                selectedTab === 'votes'
                  ? 'border-b-2 border-yellow text-yellow'
                  : 'text-neutral-400 hover:text-white'
              }`}
              onClick={() => setSelectedTab('votes')}
            >
              Voting History
            </button>
            <button
              className={`px-4 py-2 text-xl ${
                selectedTab === 'proposals'
                  ? 'border-b-2 border-yellow text-yellow'
                  : 'text-neutral-400 hover:text-white'
              }`}
              onClick={() => setSelectedTab('proposals')}
            >
              Proposal History
            </button>
          </div>

          {loadingUserVotes ? (
            <Loader />
          ) : (
            <>
              {selectedTab === 'votes' && <VotesTable votes={userVotes} />}
              {selectedTab === 'proposals' && (
                <ProposalsTable proposals={userProposals} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
